/**
 * Bidirectional unit conversion engine.
 *
 * Converts any value between units by routing through the SI base unit
 * for each category. All unit string matching is case-insensitive.
 *
 * @module shared/hydraulic/physics/unit-converter
 */

import type { ValueWithUnit } from '../base/common';
import {
  // toBase functions
  lengthToMeters,
  pressureToPa,
  flowToM3s,
  densityToKgM3,
  viscosityToPaS,
  temperatureToK,
  velocityToMs,
  powerToW,
  specificHeatToJkgK,
  massFlowToKgS,
  // fromBase functions
  paToUnit,
  mToUnit,
  m3sToUnit,
  kgm3ToUnit,
  pasToUnit,
  kToUnit,
  msToUnit,
  wToUnit,
  jkgkToUnit,
  kgsToUnit,
} from './unit-conversions';

// ============================================================================
// Unit Category Type
// ============================================================================

export type UnitCategory =
  | 'length'
  | 'pressure'
  | 'flowVolumetric'
  | 'flowMass'
  | 'density'
  | 'viscosity'
  | 'temperature'
  | 'velocity'
  | 'power'
  | 'specificHeat'
  | 'head'
  | 'rotationalSpeed';

// ============================================================================
// Exhaustive Unit → Category Map
// ============================================================================

/**
 * Maps every known unit string (lowercased) to its category.
 * No pattern matching or substring searches -- only exact lookup.
 */
const UNIT_TO_CATEGORY: Map<string, UnitCategory> = new Map<string, UnitCategory>([
  // Length
  ['ft', 'length'],
  ['feet', 'length'],
  ['foot', 'length'],
  ['in', 'length'],
  ['inch', 'length'],
  ['inches', 'length'],
  ['m', 'length'],
  ['meter', 'length'],
  ['meters', 'length'],
  ['mm', 'length'],
  ['millimeter', 'length'],
  ['millimeters', 'length'],
  ['cm', 'length'],
  ['centimeter', 'length'],
  ['centimeters', 'length'],

  // Pressure (absolute and gauge variants)
  // Absolute: psi, psia, kpa, kpaa, bar, bara, pa, atm
  // Gauge:    psig, psi g, kpag, kpa g, barg, bar g
  ['psi', 'pressure'],
  ['psig', 'pressure'],
  ['psi g', 'pressure'],
  ['psia', 'pressure'],
  ['bar', 'pressure'],
  ['bara', 'pressure'],
  ['barg', 'pressure'],
  ['bar g', 'pressure'],
  ['kpa', 'pressure'],
  ['kpaa', 'pressure'],
  ['kpag', 'pressure'],
  ['kpa g', 'pressure'],
  ['pa', 'pressure'],
  ['atm', 'pressure'],
  ['mmhg', 'pressure'],
  ['torr', 'pressure'],

  // Flow (volumetric)
  ['gpm', 'flowVolumetric'],
  ['gal/min', 'flowVolumetric'],
  ['l/min', 'flowVolumetric'],
  ['lpm', 'flowVolumetric'],
  ['l/s', 'flowVolumetric'],
  ['m\u00b3/s', 'flowVolumetric'],  // m³/s
  ['m3/s', 'flowVolumetric'],
  ['m\u00b3/h', 'flowVolumetric'],  // m³/h
  ['m3/h', 'flowVolumetric'],

  // Flow (mass)
  ['kg/s', 'flowMass'],
  ['lb/s', 'flowMass'],
  ['lbs/s', 'flowMass'],
  ['lb/h', 'flowMass'],
  ['lbs/h', 'flowMass'],
  ['lb/hr', 'flowMass'],
  ['lbs/hr', 'flowMass'],

  // Density
  ['lb/ft\u00b3', 'density'],  // lb/ft³
  ['lb/ft3', 'density'],
  ['lbm/ft3', 'density'],
  ['lbm/ft\u00b3', 'density'],  // lbm/ft³
  ['kg/m\u00b3', 'density'],  // kg/m³
  ['kg/m3', 'density'],
  ['g/cm3', 'density'],
  ['g/cm\u00b3', 'density'],  // g/cm³

  // Viscosity
  ['cp', 'viscosity'],
  ['centipoise', 'viscosity'],
  ['pa\u00b7s', 'viscosity'],  // Pa·s
  ['pa*s', 'viscosity'],
  ['pas', 'viscosity'],

  // Temperature
  ['degf', 'temperature'],
  ['\u00b0f', 'temperature'],  // °F
  ['f', 'temperature'],
  ['degc', 'temperature'],
  ['\u00b0c', 'temperature'],  // °C
  ['c', 'temperature'],
  ['k', 'temperature'],
  ['kelvin', 'temperature'],

  // Velocity
  ['ft/s', 'velocity'],
  ['fps', 'velocity'],
  ['m/s', 'velocity'],

  // Power
  ['hp', 'power'],
  ['kw', 'power'],
  ['w', 'power'],
  ['watt', 'power'],
  ['watts', 'power'],
  ['btu/h', 'power'],
  ['btu/hr', 'power'],
  ['btu/hour', 'power'],

  // Specific heat
  ['btu/(lb\u00b7degf)', 'specificHeat'],  // BTU/(lb·degF)
  ['btu/(lb\u00b7\u00b0f)', 'specificHeat'],  // BTU/(lb·°F)
  ['btu/lb\u00b7degf', 'specificHeat'],  // BTU/lb·degF
  ['btu/lb\u00b7\u00b0f', 'specificHeat'],  // BTU/lb·°F
  ['btu/(lb-degf)', 'specificHeat'],
  ['btu/(lb-\u00b0f)', 'specificHeat'],  // BTU/(lb-°F)
  ['kj/(kg\u00b7k)', 'specificHeat'],  // kJ/(kg·K)
  ['kj/(kg\u00b7\u00b0k)', 'specificHeat'],  // kJ/(kg·°K)
  ['kj/(kg-k)', 'specificHeat'],
  ['j/(kg\u00b7k)', 'specificHeat'],  // J/(kg·K)
  ['j/(kg\u00b7\u00b0k)', 'specificHeat'],  // J/(kg·°K)
  ['j/(kg-k)', 'specificHeat'],

  // Head (same units as length but separate category)
  // Head uses lengthToMeters/mToUnit internally, so same unit strings apply.
  // Callers specify head category explicitly via detectCategory override or
  // the converter routes through length since the units are identical.

  // Rotational speed (pump curve test_speed, operating_speed)
  // SI base is rpm (revolutions per minute). 1 Hz = 60 rpm.
  ['rpm', 'rotationalSpeed'],
  ['hz', 'rotationalSpeed'],
]);

// ============================================================================
// Category → Conversion Function Routing
// ============================================================================

function rotationalSpeedToRpm(value: number, unit: string): number {
  const u = unit.toLowerCase().trim();
  if (u === 'rpm') return value;
  if (u === 'hz') return value * 60;
  return value; // unreachable (UNIT_TO_CATEGORY gates entry)
}

function rpmToUnit(rpm: number, unit: string): number {
  const u = unit.toLowerCase().trim();
  if (u === 'rpm') return rpm;
  if (u === 'hz') return rpm / 60;
  return rpm;
}

function toBase(value: number, unit: string, category: UnitCategory): number {
  switch (category) {
    case 'length':
    case 'head':
      return lengthToMeters(value, unit);
    case 'pressure':
      return pressureToPa(value, unit);
    case 'flowVolumetric':
      return flowToM3s(value, unit);
    case 'flowMass':
      return massFlowToKgS(value, unit);
    case 'density':
      return densityToKgM3(value, unit);
    case 'viscosity':
      return viscosityToPaS(value, unit);
    case 'temperature':
      return temperatureToK(value, unit);
    case 'velocity':
      return velocityToMs(value, unit);
    case 'power':
      return powerToW(value, unit);
    case 'specificHeat':
      return specificHeatToJkgK(value, unit);
    case 'rotationalSpeed':
      return rotationalSpeedToRpm(value, unit);
  }
}

function fromBase(siValue: number, unit: string, category: UnitCategory): number {
  switch (category) {
    case 'length':
    case 'head':
      return mToUnit(siValue, unit);
    case 'pressure':
      return paToUnit(siValue, unit);
    case 'flowVolumetric':
      return m3sToUnit(siValue, unit);
    case 'flowMass':
      return kgsToUnit(siValue, unit);
    case 'density':
      return kgm3ToUnit(siValue, unit);
    case 'viscosity':
      return pasToUnit(siValue, unit);
    case 'temperature':
      return kToUnit(siValue, unit);
    case 'velocity':
      return msToUnit(siValue, unit);
    case 'power':
      return wToUnit(siValue, unit);
    case 'specificHeat':
      return jkgkToUnit(siValue, unit);
    case 'rotationalSpeed':
      return rpmToUnit(siValue, unit);
  }
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Detect the unit category for a unit string.
 * Returns undefined if the unit is not recognized.
 */
export function detectCategory(unitString: string): UnitCategory | undefined {
  return UNIT_TO_CATEGORY.get(unitString.toLowerCase().trim());
}

/**
 * Convert a value from one unit to another.
 * Detects the category automatically, converts via the SI base unit.
 * Returns the value unchanged if units are the same or unrecognized.
 */
export function convertValue(value: number, fromUnit: string, toUnit: string): number {
  if (fromUnit === toUnit) return value;

  const fromLower = fromUnit.toLowerCase().trim();
  const toLower = toUnit.toLowerCase().trim();
  if (fromLower === toLower) return value;

  const category = detectCategory(fromLower);
  if (!category) return value; // unknown unit, return as-is

  const siValue = toBase(value, fromUnit, category);
  return fromBase(siValue, toUnit, category);
}

/**
 * Return the SI base unit string for a given category.
 */
export function getBaseUnit(category: UnitCategory): string {
  switch (category) {
    case 'length': return 'm'
    case 'head': return 'm'
    case 'pressure': return 'Pa'
    case 'flowVolumetric': return 'm3/s'
    case 'flowMass': return 'kg/s'
    case 'density': return 'kg/m3'
    case 'viscosity': return 'Pa·s'
    case 'temperature': return 'K'
    case 'velocity': return 'm/s'
    case 'power': return 'W'
    case 'specificHeat': return 'J/(kg·K)'
    case 'rotationalSpeed': return 'rpm'
  }
}

/**
 * Convert a value to SI base units. Returns { value, unit } in the SI base unit.
 * If category is unknown, returns the value unchanged with its original unit.
 */
export function toSI(value: number, fromUnit: string): { value: number; unit: string } {
  const category = detectCategory(fromUnit)
  if (!category) return { value, unit: fromUnit }
  const baseUnit = getBaseUnit(category)
  return { value: convertValue(value, fromUnit, baseUnit), unit: baseUnit }
}

/**
 * Read a unit-bearing field as its SI base number (#850).
 *
 * The three functions below are the only sanctioned ways to take the number
 * out of a `{ value, unit }` pair; the `no-restricted-syntax` rule in
 * `frontend/eslint.config.js` and `backend/eslint.config.js` rejects a raw
 * `zone.density.value` read, because that is the shape every unit bug in this
 * codebase has had — a number separated from the unit that said what it meant
 * (the 16x lbm/ft³ density, the 1000x cP viscosity, the 1.3 mi trunk main read
 * as feet).
 *
 * `category` is REQUIRED and not sniffed from the unit string: 'm' is a
 * length, 'in' is a length, and both are also perfectly good nonsense for a
 * pressure. Naming the category at the call site is what makes the read
 * checkable by a reader.
 *
 * Throws for the categories whose toBase converter throws on an unrecognized
 * unit (length, density, viscosity, volumetric flow, mass flow, power).
 */
export function readSI(vwu: ValueWithUnit, category: UnitCategory): number {
  return toBase(vwu.value, vwu.unit, category);
}

/**
 * Read a unit-bearing field as a number in `targetUnit` (#850).
 *
 * The display counterpart of {@link readSI}: same conversion `convertValue`
 * performs, but the value and its unit are taken from the pair together so
 * they cannot be mismatched. Passing the field's own unit is a legitimate
 * identity read — it says "this number is being used in the unit it is
 * stored in", which is exactly what the reviewer needs to know.
 */
export function readIn(vwu: ValueWithUnit, targetUnit: string): number {
  return convertValue(vwu.value, vwu.unit, targetUnit);
}

/**
 * `"<value> <unit>"` — the quantity as stored, unconverted (#850).
 *
 * For the display sites that print a number next to its own unit. With
 * `decimals` omitted the number is stringified exactly as a template literal
 * would, so this is a drop-in for `` `${x.value} ${x.unit}` ``; with
 * `decimals` it is `toFixed`, a drop-in for `` `${x.value.toFixed(n)} ${x.unit}` ``.
 * Use {@link formatValueInUnit} instead when the value must be CONVERTED
 * before display.
 */
export function formatVwu(vwu: ValueWithUnit, decimals?: number): string {
  const shown = decimals === undefined ? `${vwu.value}` : vwu.value.toFixed(decimals);
  return `${shown} ${vwu.unit}`;
}

/**
 * Format a ValueWithUnit into a target unit with specified decimal places.
 */
export function formatValueInUnit(
  vwu: ValueWithUnit,
  targetUnit: string,
  decimals: number = 2,
): { value: number; unit: string; display: string } {
  const converted = convertValue(vwu.value, vwu.unit, targetUnit);
  const rounded = Math.round(converted * 10 ** decimals) / 10 ** decimals;
  return { value: rounded, unit: targetUnit, display: `${rounded} ${targetUnit}` };
}

/**
 * Convert a ValueWithUnit to a new target unit, returning a new ValueWithUnit.
 */
export function convertVwu(vwu: ValueWithUnit, targetUnit: string): ValueWithUnit {
  return { value: convertValue(vwu.value, vwu.unit, targetUnit), unit: targetUnit };
}

/**
 * Convert mass flow (kg/s) to display volumetric flow in a target unit.
 *
 * This is the density-aware helper that replaces all hardcoded kgSToGpm() calls.
 * It divides mass flow by density to get m3/s, then converts to the target unit.
 *
 * @param massFlowKgS - Mass flow rate in kg/s
 * @param densityKgM3 - Fluid density in kg/m3
 * @param targetUnit - Target volumetric flow unit (e.g. 'gpm' or 'm3/h')
 */
export function massFlowToDisplayVolFlow(
  massFlowKgS: number,
  densityKgM3: number,
  targetUnit: string,
): { value: number; unit: string } {
  const m3s = massFlowKgS / densityKgM3;
  return { value: convertValue(m3s, 'm\u00b3/s', targetUnit), unit: targetUnit };
}
